import PropTypes from 'prop-types';
import { Typography, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import AttributesList from '../../../attributesList';
import NoDataText from '../../../noDataText';
import Scrollbars from '../../../scrollbars';
import UserAvatar from '../../../userAvatar';
import { isArrayEmpty } from '../../../../../utility/helpers';
import { formatDateRange } from '../../../customDateRange/config';

const PRESET_SUMMARY_MAX_HEIGHT = 108;
const PRESET_SUMMARY_MAX_HEIGHT_XLG = 400;

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    root: {
      minHeight: 0,
      height: 'auto',
      maxHeight: PRESET_SUMMARY_MAX_HEIGHT,
      width: '100%',
      [breakpoints.up('xl')]: {
        maxHeight: PRESET_SUMMARY_MAX_HEIGHT_XLG,
      },
    },
    rootOverlay: {
      height: 'auto',
      maxHeight: PRESET_SUMMARY_MAX_HEIGHT,
      [breakpoints.up('xl')]: {
        maxHeight: PRESET_SUMMARY_MAX_HEIGHT_XLG,
      },
    },
    rootViewport: {
      maxHeight: PRESET_SUMMARY_MAX_HEIGHT,
      [breakpoints.up('xl')]: {
        maxHeight: PRESET_SUMMARY_MAX_HEIGHT_XLG,
      },
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing(2),
      minHeight: 0,
    },
    section: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing(1),
      minHeight: 0,
    },
    listSection: {
      minHeight: 0,
    },
    sectionLabel: {
      marginBottom: 0,
    },
    scrollY: {
      right: -8,
    },
    attributesList: {
      width: '100%',
    },
    users: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing(1),
      width: '100%',
    },
    userItem: {
      width: '100%',
    },
    userAvatar: {
      width: '100%',
    },
    userName: {
      fontSize: 14,
      lineHeight: '18px',
    },
    labelsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing(1),
      width: '100%',
    },
    value: {
      margin: 0,
    },
    compareFiltersRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gridColumnGap: spacing(2),
      minHeight: 0,
      minWidth: 0,
    },
    compareFilterGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing(2),
      minHeight: 0,
      minWidth: 0,
      '&:not(:first-child)': {
        paddingLeft: spacing(2),
        borderLeft: `1px solid ${primary.bluish7}`,
      },
    },
    compareFilterTitle: {
      marginBottom: 0,
    },
  })
);

export const PRESET_SUMMARY_DATE_FORMAT = 'MMM d, yyyy';

const PresetSummary = ({
  translations,
  nothingSelected,
  surveyTypes = '',
  hasSurveyType = false,
  hasRange = false,
  hasAttributes = false,
  hasSingleAttribute = false,
  hasScores = false,
  hasUserList = false,
  hasSubjectList = false,
  hasReviewerList = false,
  hasCompareFilters = false,
  hasFilterSections = false,
  range = null,
  attributes = [],
  attribute = null,
  scores = [],
  users = [],
  subjectUsers = [],
  reviewerUsers = [],
  compareFilters = [],
  filterSections = [],
  checkIsSelectable = () => true,
}) => {
  const classes = useStyles();

  const getRangeLabel = dateRange =>
    formatDateRange(dateRange, PRESET_SUMMARY_DATE_FORMAT);

  const renderLabelsList = items => {
    if (isArrayEmpty(items)) {
      return <NoDataText text={nothingSelected} variant="body2" />;
    }

    return (
      <div className={classes.labelsList}>
        {items.map(item => (
          <Typography
            key={`preset_label_${item.id}`}
            className={classes.value}
            variant="body2"
          >
            {item.name}
          </Typography>
        ))}
      </div>
    );
  };

  const renderReviewersList = reviewers => {
    if (isArrayEmpty(reviewers)) {
      return <NoDataText text={nothingSelected} variant="body2" />;
    }

    return (
      <div className={classes.users}>
        {reviewers.map(user => (
          <div key={`preset_user_${user.id}`} className={classes.userItem}>
            <UserAvatar
              className={classes.userAvatar}
              labelClass={classes.userName}
              user={user}
              caption
              small
            />
          </div>
        ))}
      </div>
    );
  };

  const renderTextValue = value =>
    value ? (
      <Typography className={classes.value} variant="body2">
        {value}
      </Typography>
    ) : (
      <NoDataText text={nothingSelected} variant="body2" />
    );

  return (
    <Scrollbars
      className={classes.root}
      overlayWrapperClass={classes.rootOverlay}
      viewportClass={classes.rootViewport}
      verticalScrollClass={classes.scrollY}
    >
      <div className={classes.content}>
        {hasFilterSections &&
          (isArrayEmpty(filterSections) ? (
            <NoDataText text={nothingSelected} variant="body2" />
          ) : (
            filterSections.map(section => (
              <div
                key={section.title}
                className={classNames(classes.section, classes.listSection)}
              >
                <Typography
                  className={classes.sectionLabel}
                  variant="subtitle2"
                >
                  {section.title}
                </Typography>
                {section.isUserList
                  ? renderReviewersList(section.users)
                  : renderLabelsList(section.items)}
              </div>
            ))
          ))}
        {hasCompareFilters && (
          <div className={classes.compareFiltersRow}>
            {compareFilters.map(filter => (
              <div
                key={filter.title}
                className={classNames(
                  classes.section,
                  classes.compareFilterGroup
                )}
              >
                <Typography
                  className={classes.compareFilterTitle}
                  variant="subtitle1"
                >
                  {filter.title}
                </Typography>
                {filter.subject ? (
                  <div className={classes.userItem}>
                    <UserAvatar
                      className={classes.userAvatar}
                      labelClass={classes.userName}
                      user={filter.subject}
                      caption
                      small
                    />
                  </div>
                ) : (
                  <NoDataText text={nothingSelected} variant="body2" />
                )}
                <div className={classes.section}>
                  <Typography
                    className={classes.sectionLabel}
                    variant="subtitle2"
                  >
                    {translations.surveyType}
                  </Typography>
                  {renderTextValue(filter.surveyTypes)}
                </div>
                <div className={classes.section}>
                  <Typography
                    className={classes.sectionLabel}
                    variant="subtitle2"
                  >
                    {translations.range}
                  </Typography>
                  {renderTextValue(getRangeLabel(filter.range))}
                </div>
                {filter.showReviewers && (
                  <div
                    className={classNames(classes.section, classes.listSection)}
                  >
                    <Typography
                      className={classes.sectionLabel}
                      variant="subtitle2"
                    >
                      {translations.reviewers}
                    </Typography>
                    {renderReviewersList(filter.reviewers)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {hasSurveyType && (
          <div className={classes.section}>
            <Typography className={classes.sectionLabel} variant="subtitle2">
              {translations.surveyType}
            </Typography>
            {renderTextValue(surveyTypes)}
          </div>
        )}
        {hasRange && (
          <div className={classes.section}>
            <Typography className={classes.sectionLabel} variant="subtitle2">
              {translations.range}
            </Typography>
            {renderTextValue(getRangeLabel(range))}
          </div>
        )}
        {hasSingleAttribute && (
          <div className={classNames(classes.section, classes.listSection)}>
            <Typography className={classes.sectionLabel} variant="subtitle2">
              {translations.attribute}
            </Typography>
            {!attribute?.id ? (
              <NoDataText text={nothingSelected} variant="body2" />
            ) : (
              <AttributesList
                className={classes.attributesList}
                attributes={[attribute]}
                selectedAttributes={[attribute]}
                isHorizontalList
              />
            )}
          </div>
        )}
        {hasAttributes && (
          <div className={classNames(classes.section, classes.listSection)}>
            <Typography className={classes.sectionLabel} variant="subtitle2">
              {translations.attributes}
            </Typography>
            {isArrayEmpty(attributes) ? (
              <NoDataText text={nothingSelected} variant="body2" />
            ) : (
              <AttributesList
                className={classes.attributesList}
                attributes={attributes}
                selectedAttributes={attributes}
                checkIsSelectable={checkIsSelectable}
                isHorizontalList
              />
            )}
          </div>
        )}
        {hasScores && (
          <div className={classes.section}>
            <Typography className={classes.sectionLabel} variant="subtitle2">
              {translations.scores}
            </Typography>
            {renderTextValue(!isArrayEmpty(scores) ? scores.join(', ') : '')}
          </div>
        )}
        {hasSubjectList && (
          <div className={classNames(classes.section, classes.listSection)}>
            <Typography className={classes.sectionLabel} variant="subtitle2">
              {translations.subject}
            </Typography>
            {renderReviewersList(subjectUsers)}
          </div>
        )}
        {hasReviewerList && (
          <div className={classNames(classes.section, classes.listSection)}>
            <Typography className={classes.sectionLabel} variant="subtitle2">
              {translations.reviewers}
            </Typography>
            {renderReviewersList(reviewerUsers)}
          </div>
        )}
        {hasUserList && (
          <div className={classNames(classes.section, classes.listSection)}>
            <Typography className={classes.sectionLabel} variant="subtitle2">
              {translations.people}
            </Typography>
            {renderReviewersList(users)}
          </div>
        )}
      </div>
    </Scrollbars>
  );
};

const dateRangeShape = PropTypes.shape({
  startDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
});

PresetSummary.propTypes = {
  translations: PropTypes.shape({
    surveyType: PropTypes.string,
    range: PropTypes.string,
    attribute: PropTypes.string,
    attributes: PropTypes.string,
    scores: PropTypes.string,
    people: PropTypes.string,
    subject: PropTypes.string,
    reviewers: PropTypes.string,
  }).isRequired,
  hasSurveyType: PropTypes.bool,
  hasRange: PropTypes.bool,
  hasAttributes: PropTypes.bool,
  hasSingleAttribute: PropTypes.bool,
  hasScores: PropTypes.bool,
  hasUserList: PropTypes.bool,
  hasSubjectList: PropTypes.bool,
  hasReviewerList: PropTypes.bool,
  hasCompareFilters: PropTypes.bool,
  hasFilterSections: PropTypes.bool,
  filterSections: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      isUserList: PropTypes.bool,
      users: PropTypes.arrayOf(PropTypes.object),
      items: PropTypes.arrayOf(PropTypes.object),
    })
  ),
  compareFilters: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      subject: PropTypes.object,
      surveyTypeLabel: PropTypes.string,
      range: dateRangeShape,
      showReviewers: PropTypes.bool,
      reviewers: PropTypes.arrayOf(PropTypes.object),
    })
  ),
  surveyTypes: PropTypes.string,
  range: dateRangeShape,
  attributes: PropTypes.arrayOf(PropTypes.object),
  attribute: PropTypes.object,
  scores: PropTypes.arrayOf(PropTypes.number),
  users: PropTypes.arrayOf(PropTypes.object),
  subjectUsers: PropTypes.arrayOf(PropTypes.object),
  reviewerUsers: PropTypes.arrayOf(PropTypes.object),
  checkIsSelectable: PropTypes.func,
  nothingSelected: PropTypes.string.isRequired,
};

export default PresetSummary;
